import type { PainelFilaItem } from "../../../services/painelService";
import type { FornoItem } from "../../../services/fornoService";
import type { AguardandoEntregaItem } from "../../../services/aguardandoEntregaService";
import { BrazilDate } from "../../../utils/BrazilDate";
import {
  contarProntosEntrega,
  dataInicioAguardandoEntregaPedido,
} from "../utils/agruparPedidosPainel";
import {
  segundosDesdeInicio,
  segundosEntreInstantes,
} from "./temposPainel";

/** Até 2 min na coluna: visual normal. */
export const PAINEL_URGENCIA_NORMAL_MAX_SEG = 120;

/** De 2 min até antes de 6 min: laranja. */
export const PAINEL_URGENCIA_CRITICO_MIN_SEG = 360;

export type PainelCardUrgencia = "normal" | "alerta" | "critico";

export function resolvePainelCardUrgencia(
  segundosNaColuna: number | null | undefined,
): PainelCardUrgencia {
  if (segundosNaColuna == null || segundosNaColuna < 0) return "normal";
  if (segundosNaColuna <= PAINEL_URGENCIA_NORMAL_MAX_SEG) return "normal";
  if (segundosNaColuna < PAINEL_URGENCIA_CRITICO_MIN_SEG) return "alerta";
  return "critico";
}

export function resolveTempoColunaFilaMontagem(
  item: PainelFilaItem,
  nowMs: number,
): number | null {
  const montagem = item.montagens?.[0];
  const fila = item as {
    startedAt?: string | null;
    finishedAt?: string | null;
    tempoTotal?: number | null;
  };
  const createdAt = item.createdAt;
  const inicioMontagem =
    item.status === "in_progress"
      ? montagem?.startedAt ?? fila.startedAt ?? null
      : null;
  const fimMontagem = montagem?.finishedAt ?? fila.finishedAt ?? null;
  const tempoTotal = montagem?.tempoTotal ?? fila.tempoTotal ?? null;

  if (item.status === "pending" && createdAt) {
    return segundosDesdeInicio(createdAt, nowMs);
  }

  if (!inicioMontagem) return null;

  if (fimMontagem) {
    return tempoTotal != null && tempoTotal >= 0
      ? tempoTotal
      : segundosEntreInstantes(inicioMontagem, fimMontagem);
  }

  return segundosDesdeInicio(inicioMontagem, nowMs);
}

export function resolveSegundosNoForno(
  item: FornoItem,
  nowMs: number,
): number | null {
  const inicioRef = item.startedAt ?? item.createdAt;
  if (!inicioRef) return null;

  const startMs = new Date(inicioRef).getTime();
  if (Number.isNaN(startMs)) return null;

  if (item.finishedAt) {
    const fimMs = new Date(item.finishedAt).getTime();
    if (!Number.isNaN(fimMs)) {
      return Math.max(0, Math.round((fimMs - startMs) / 1000));
    }
    if (item.tempoForno != null && item.tempoForno >= 0) {
      return item.tempoForno;
    }
  }

  return Math.max(0, Math.round((nowMs - startMs) / 1000));
}

function segundosEntreEntrega(inicioIso: string, fimMs: number): number {
  return Math.max(
    0,
    Math.round(
      (fimMs - BrazilDate.parseAsBrazil(inicioIso).getTime()) / 1000,
    ),
  );
}

export function resolveTempoColunaEntrega(
  itens: AguardandoEntregaItem[],
  fase: "aguardando_entrega" | "em_entrega",
  nowMs: number,
): number | null {
  if (fase === "aguardando_entrega") {
    const { todosProntos } = contarProntosEntrega(itens);
    if (!todosProntos) return null;

    const inicio = dataInicioAguardandoEntregaPedido(itens);
    if (!inicio) return null;

    const concluido = itens.find((item) => item.dataFim != null);
    if (concluido?.dataFim) {
      return Math.round(
        (BrazilDate.parseAsBrazil(concluido.dataFim).getTime() -
          BrazilDate.parseAsBrazil(inicio).getTime()) /
          1000,
      );
    }
    return segundosEntreEntrega(inicio, nowMs);
  }

  const valores = itens.map((item) => {
    if (item.entregaDataInicio != null) {
      return segundosEntreEntrega(item.entregaDataInicio, nowMs);
    }
    return null;
  });

  const nums = valores.filter((v): v is number => v != null && v >= 0);
  return nums.length > 0 ? Math.max(...nums) : null;
}

export const PAINEL_URGENCIA_SHELL_CLASS: Record<PainelCardUrgencia, string> = {
  normal:
    "overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm",
  alerta:
    "overflow-hidden rounded-xl border border-orange-300 bg-orange-50 shadow-sm",
  critico:
    "overflow-hidden rounded-xl border border-red-400 bg-red-50 shadow-sm",
};

export const PAINEL_URGENCIA_BUTTON_CLASS: Record<PainelCardUrgencia, string> = {
  normal:
    "flex w-full flex-col rounded-lg border border-amber-200 bg-amber-50/90 p-3 text-left shadow-sm transition hover:bg-amber-100",
  alerta:
    "flex w-full flex-col rounded-lg border border-orange-300 bg-orange-50 p-3 text-left shadow-sm transition hover:bg-orange-100",
  critico:
    "flex w-full flex-col rounded-lg border border-red-400 bg-red-50 p-3 text-left shadow-sm transition hover:bg-red-100",
};
